import { CurrencyValue, FieldValue, HookedRecordResult, type Row } from '@getnuvo/importer-react'
import moment from 'moment'

import i18n from 'modules/core/lib/i18n'

import { Country } from 'modules/legal_entities/lib/constants'
import idValidationStrategies from 'modules/legal_entities/lib/identifierValidators'
// Import all IdTypes through a barrel import
import * as IdTypes from 'modules/legal_entities/lib/identifierValidators/idTypes'
import { IdentificationType } from 'modules/legal_entities/lib/types'
import { Metadata } from 'modules/universal_importer/components/NuvoImporter/types'

import {
  type CompatibleColumns,
  createHookedRecordResult,
  findColumnByKey,
  getFieldValue,
  hasKey,
} from './helpers'

export type ValidationFunction = (
  row: Record<string, FieldValue | CurrencyValue | HookedRecordResult>,
  metadata: Metadata,
  columns: CompatibleColumns,
  currentColumnKey: string,
  parameters?: { key: string; value: string }[] | null,
  changeLog?: Row | null
) => { value: FieldValue | CurrencyValue | HookedRecordResult }

// Fallback formats when outputFormat is not specified (order matters - most specific first)
const FALLBACK_DATE_FORMATS = [
  'YYYY-MM-DD', // ISO format (backend)
  'DD/MM/YYYY', // European format
  'MM/DD/YYYY', // US format
  'DD-MM-YYYY',
  'DD.MM.YYYY',
  'YYYY/MM/DD',
]

// Helper to parse date strings based on outputFormat or with fallback formats
const parseImportDate = (
  dateString: string,
  outputFormat?: string | null
): moment.Moment | null => {
  // If we have a specific outputFormat, use it directly
  if (outputFormat) {
    const parsed = moment(dateString, outputFormat, true)
    return parsed.isValid() ? parsed : null
  }

  // Fallback: try multiple formats
  const parsed = moment(dateString, FALLBACK_DATE_FORMATS, true)
  return parsed.isValid() ? parsed : null
}

// Helper to get outputFormat from columns array
const getOutputFormat = (columns: CompatibleColumns, columnKey: string): string | null => {
  const column = findColumnByKey(columns, columnKey)
  if (hasKey(column) && 'outputFormat' in column && typeof column.outputFormat === 'string') {
    return column.outputFormat
  }
  return null
}

// Helper to safely convert string to Country enum
const getCountryFromString = (countryString: string): Country | undefined => {
  const countryValues = Object.values(Country)
  const foundCountry = countryValues.find(
    (countryValue) => String(countryValue).toLowerCase() === countryString.toLowerCase()
  )
  return foundCountry
}

// Helper to get tax_id type based on country
const getTaxIdTypeForCountry = (country: string): string => {
  switch (country.toLowerCase()) {
    case 'de':
    case 'germany':
      return 'steuer_id'
    default:
      return 'tax_id'
  }
}

// Helper to determine id_type based on context
const getIdentifierType = (
  row: Record<string, FieldValue | CurrencyValue | HookedRecordResult>,
  currentColumnKey: string,
  country: string
): string => {
  // Apply identifier-specific logic
  switch (currentColumnKey.toLowerCase()) {
    case 'identifier':
      return String(getFieldValue(row, 'identifier_type') || '')

    case 'social_security_number':
      return 'social_security_number'
    case 'tax_id':
      return getTaxIdTypeForCountry(country)
    default:
      return ''
  }
}

// Complete integration with legal_entities idValidationStrategies
const validateIdentifier = (country: string, idType: string, id: string, idLabel: string) => {
  try {
    // Convert string parameters to proper types
    const validCountry = getCountryFromString(country) || undefined
    // Early validation - be explicit about invalid data
    if (!validCountry) {
      return {
        isValid: false,
        errors: i18n.t('importer_process.edition_step.filters.rows.options.unrecognized_country'),
      }
    }

    const validIdType = isValidIdentificationType(idType, country) ? idType : undefined
    if (!validIdType) {
      return {
        isValid: false,
        errors: i18n.t('employee_form.choose_identifier_type'),
      }
    }

    // Call the actual validation strategy from legal_entities
    const strategy = idValidationStrategies(validCountry, validIdType, id, idLabel)
    return {
      isValid: strategy.isValid(),
      errors: strategy.errors(),
    }
  } catch (error) {
    // Return the actual error from legal_entities validation
    return {
      isValid: false,
      errors: `Validation error: ${error instanceof Error ? error.message : String(error)}`,
    }
  }
}

function isValidIdentificationType(value: string, country: string): value is IdentificationType {
  // Map country codes to Country enum values
  const getCountryEnum = (countryCode: string): Country | undefined => {
    return Object.values(Country).find((c) => c.toLowerCase() === countryCode.toLowerCase())
  }

  const countryEnum = getCountryEnum(country)
  if (!countryEnum) return false

  // Extract country name from enum (e.g., Country.Spain -> "Spain")
  const countryName = Object.entries(Country).find(
    ([, countryValue]) => countryValue === countryEnum
  )?.[0]
  if (!countryName) return false

  // Dynamically construct IdType name: "Spain" + "IdType" = "SpainIdType"
  const idTypeName = `${countryName}IdType`

  // Access the IdType object dynamically
  const IdTypeObject = Reflect.get(IdTypes, idTypeName)
  if (!IdTypeObject) return false

  const validIdTypes = Object.values(IdTypeObject)
  return validIdTypes.some((idType) => idType === value)
}

// Helper to get id type label from columns or translation
const getIdTypeLabel = (
  columns: CompatibleColumns,
  idType: string,
  currentColumnKey: string
): string => {
  // Find identifier type column to get dropdown options for label
  const idTypeColumn = findColumnByKey(columns, 'identifier_type')
  const dropdownOptions =
    hasKey(idTypeColumn) && Array.isArray(idTypeColumn.dropdownOptions)
      ? idTypeColumn.dropdownOptions
      : []

  switch (currentColumnKey.toLowerCase()) {
    case 'identifier':
      for (const option of dropdownOptions) {
        if (option.value === idType) {
          return String(option.label || idType)
        }
      }
      return idType

    case 'social_security_number':
      return i18n.t(`import.table.headers.social_security_number`)

    case 'tax_id':
      return i18n.t(`import.table.headers.tax_id`)

    default:
      return idType
  }
}

const identifier: ValidationFunction = (
  row: Record<string, FieldValue | CurrencyValue | HookedRecordResult>,
  _metadata: Metadata,
  columns: CompatibleColumns,
  currentColumnKey: string,
  _parameters?: { key: string; value: string }[] | null
) => {
  // Extract parameters using all available columns
  const country = String(getFieldValue(row, 'legal_entity_country') || '')
  const idType = getIdentifierType(row, currentColumnKey, country)
  const id = String(getFieldValue(row, currentColumnKey) || '')

  const idLabel = getIdTypeLabel(columns, idType, currentColumnKey)
  let isValid = true
  let errors = ''

  if (idType) {
    if (currentColumnKey === 'identifier' && !id) {
      // Required validation for identifier if id_type informed
      isValid = false
      errors = i18n.t('employee_form.invalid_number')
    } else {
      const validCountry = getCountryFromString(country) || undefined
      // Use the helper function to validate the identifier safely
      if (validCountry) {
        ;({ isValid, errors } = validateIdentifier(country, idType, id, idLabel))
      }
    }
  }

  // Return the original value if valid, or an error indicator if invalid
  const value = isValid
    ? row[currentColumnKey]
    : createHookedRecordResult(getFieldValue(row, currentColumnKey), errors)

  return { value }
}

const range: ValidationFunction = (
  row: Record<string, FieldValue | CurrencyValue | HookedRecordResult>,
  _metadata: Metadata,
  _columns: CompatibleColumns,
  currentColumnKey: string,
  parameters?: { key: string; value: string }[] | null
) => {
  const numberToValidateValue = getFieldValue(row, currentColumnKey) ?? ''
  const numberToValidate = parseFloat(numberToValidateValue.toString())
  if (numberToValidate == null || isNaN(numberToValidate)) {
    return { value: row[currentColumnKey] }
  }

  // Extract parameters
  const default_min = -(2 ** (64 - 1)) // Equivalent to Ruby's -(2**((0.size) * 8 - 1))
  const default_max = 2 ** (64 - 1) - 1 // Equivalent to Ruby's ((2**((0.size) * 8 - 1)) - 1)
  const minValue = parameters?.find((p) => p.key === 'min')?.value || default_min
  const maxValue = parameters?.find((p) => p.key === 'max')?.value || default_max
  const min = parseFloat(minValue.toString())
  const max = parseFloat(maxValue.toString())

  // Return the original value if valid, or an error indicator if invalid
  const value =
    numberToValidate >= min && numberToValidate <= max
      ? row[currentColumnKey]
      : createHookedRecordResult(
          getFieldValue(row, currentColumnKey),
          i18n.t('universal_importer.nuvo_importer.validations.value_is_not_in_range')
        )

  return { value }
}

const policy: ValidationFunction = (
  row: Record<string, FieldValue | CurrencyValue | HookedRecordResult>,
  _metadata: Metadata,
  _columns: CompatibleColumns,
  currentColumnKey: string,
  parameters?: { key: string; value: string }[] | null,
  changeLog?: Row | null
) => {
  const policyObject = parameters?.find((p) => p.key === 'policy')?.value || ''
  const policyName = parameters?.find((p) => p.key === 'policyName')?.value || ''
  const policyJsonString = getFieldValue(row, policyObject) || '{}'

  let policyData: Record<string, unknown> = {}
  try {
    policyData = JSON.parse(String(policyJsonString))
  } catch (error) {
    // If the JSON cannot be parsed, use an empty object
    policyData = {}
  }

  // Check if the policy allows the field
  const isPolicyAllowed = policyData[policyName] === true
  const isValueChanged =
    changeLog && currentColumnKey in changeLog ? getFieldValue(row, currentColumnKey) != '' : false

  // If the policy does not allow the field AND the value has changed, return an error
  const value =
    isPolicyAllowed || !isValueChanged
      ? row[currentColumnKey]
      : createHookedRecordResult(
          getFieldValue(row, currentColumnKey),
          i18n.t('universal_importer.nuvo_importer.validations.policy_denied')
        )

  return { value }
}

const date_after_or_equal: ValidationFunction = (
  row: Record<string, FieldValue | CurrencyValue | HookedRecordResult>,
  _metadata: Metadata,
  columns: CompatibleColumns,
  currentColumnKey: string,
  parameters?: { key: string; value: string }[] | null
) => {
  const dateValue = getFieldValue(row, currentColumnKey)

  // If empty, let required validation handle it
  if (!dateValue) {
    return { value: row[currentColumnKey] }
  }

  const outputFormat = getOutputFormat(columns, currentColumnKey)
  const dateString = dateValue.toString()
  const inputDate = parseImportDate(dateString, outputFormat)

  // Check if date is valid
  if (!inputDate) {
    return {
      value: createHookedRecordResult(
        dateValue,
        i18n.t('universal_importer.nuvo_importer.validations.invalid_date_format')
      ),
    }
  }

  // Get the comparison field parameter (required for this validation)
  const comparisonFieldName = parameters?.find((p) => p.key === 'comparison_field')?.value

  if (!comparisonFieldName) {
    // Missing required parameter - skip validation
    return { value: row[currentColumnKey] }
  }

  // Get the comparison field value
  const comparisonValue = getFieldValue(row, comparisonFieldName)

  if (!comparisonValue) {
    // If comparison field is empty, skip validation
    return { value: row[currentColumnKey] }
  }

  const comparisonOutputFormat = getOutputFormat(columns, comparisonFieldName)
  const comparisonDate = parseImportDate(comparisonValue.toString(), comparisonOutputFormat)

  if (!comparisonDate) {
    // If comparison date is invalid, skip validation
    return { value: row[currentColumnKey] }
  }

  // Format the comparison date for display
  const formattedComparisonDate = comparisonDate.format('L')

  // Check if date is after or equal to the comparison date (using start of day for fair comparison)
  const value = inputDate.startOf('day').isSameOrAfter(comparisonDate.startOf('day'))
    ? row[currentColumnKey]
    : createHookedRecordResult(
        dateValue,
        i18n.t('universal_importer.nuvo_importer.validations.date_must_be_on_or_after', {
          date: formattedComparisonDate,
        })
      )

  return { value }
}

const within_n_days: ValidationFunction = (
  row: Record<string, FieldValue | CurrencyValue | HookedRecordResult>,
  _metadata: Metadata,
  columns: CompatibleColumns,
  currentColumnKey: string,
  parameters?: { key: string; value: string }[] | null
) => {
  const dateValue = getFieldValue(row, currentColumnKey)

  // If empty, let required validation handle it
  if (!dateValue) {
    return { value: row[currentColumnKey] }
  }

  // Extract parameters
  const targetField = parameters?.find((p) => p.key === 'target_field')?.value
  const daysParam = parameters?.find((p) => p.key === 'days')?.value
  const direction = parameters?.find((p) => p.key === 'direction')?.value || 'both'

  if (!targetField || !daysParam) {
    // Missing required parameters
    return { value: row[currentColumnKey] }
  }

  const targetDateValue = getFieldValue(row, targetField)

  // If target field is empty, can't validate
  if (!targetDateValue) {
    return { value: row[currentColumnKey] }
  }

  const days = parseInt(daysParam, 10)
  const currentOutputFormat = getOutputFormat(columns, currentColumnKey)
  const targetOutputFormat = getOutputFormat(columns, targetField)
  const currentDate = parseImportDate(dateValue.toString(), currentOutputFormat)
  const targetDate = parseImportDate(targetDateValue.toString(), targetOutputFormat)

  // Check if dates are valid
  if (!currentDate || !targetDate) {
    return {
      value: createHookedRecordResult(
        dateValue,
        i18n.t('universal_importer.nuvo_importer.validations.invalid_date_format')
      ),
    }
  }

  // Calculate days difference using moment
  const daysDiff = currentDate.startOf('day').diff(targetDate.startOf('day'), 'days')
  const absDaysDiff = Math.abs(daysDiff)

  // Get the label for the target field from columns
  const targetColumn = findColumnByKey(columns, targetField)
  const targetFieldLabel =
    hasKey(targetColumn) && typeof targetColumn.label === 'string'
      ? targetColumn.label
      : targetField

  // Validate based on direction
  let isValid = false
  let errorMessage = ''

  if (direction === 'after') {
    isValid = daysDiff >= 0 && daysDiff <= days
    errorMessage = i18n.t(
      'universal_importer.nuvo_importer.validations.date_must_be_within_n_days_after',
      {
        days,
        targetField: targetFieldLabel,
      }
    )
  } else if (direction === 'before') {
    isValid = daysDiff <= 0 && absDaysDiff <= days
    errorMessage = i18n.t(
      'universal_importer.nuvo_importer.validations.date_must_be_within_n_days_before',
      {
        days,
        targetField: targetFieldLabel,
      }
    )
  } else {
    // both directions
    isValid = absDaysDiff <= days
    errorMessage = i18n.t(
      'universal_importer.nuvo_importer.validations.date_must_be_within_n_days',
      {
        days,
        targetField: targetFieldLabel,
      }
    )
  }

  const value = isValid ? row[currentColumnKey] : createHookedRecordResult(dateValue, errorMessage)

  return { value }
}

const required_with: ValidationFunction = (
  row: Record<string, FieldValue | CurrencyValue | HookedRecordResult>,
  _metadata: Metadata,
  columns: CompatibleColumns,
  currentColumnKey: string,
  parameters?: { key: string; value: string }[] | null
) => {
  const currentValue = getFieldValue(row, currentColumnKey)

  // Extract parameters
  const columnsParam = parameters?.find((p) => p.key === 'columns')?.value

  // Skip validation if columns parameter is not provided
  if (!columnsParam) {
    return { value: row[currentColumnKey] }
  }

  // Split columns by comma and check if any of them have a value
  const columnsArray = columnsParam.split(',').map((col) => col.trim())
  const isRequired = columnsArray.some((column) => {
    const columnValue = getFieldValue(row, column)
    return columnValue != null && String(columnValue).trim() !== ''
  })

  // Get the current field label for error message
  const currentColumn = findColumnByKey(columns, currentColumnKey)
  const currentFieldLabel =
    hasKey(currentColumn) && typeof currentColumn.label === 'string'
      ? currentColumn.label
      : currentColumnKey

  // Get labels for the required columns
  const requiredColumnsLabels = columnsArray.map((column) => {
    const columnObj = findColumnByKey(columns, column)
    return hasKey(columnObj) && typeof columnObj.label === 'string' ? columnObj.label : column
  })

  // Only fail if is_required is true AND current field is blank
  const value =
    isRequired && (!currentValue || String(currentValue).trim() === '')
      ? createHookedRecordResult(
          currentValue,
          i18n.t('universal_importer.nuvo_importer.validations.required_with', {
            field: currentFieldLabel,
            columns: requiredColumnsLabels.join(', '),
          })
        )
      : row[currentColumnKey]

  return { value }
}

const required_with_all: ValidationFunction = (
  row: Record<string, FieldValue | CurrencyValue | HookedRecordResult>,
  _metadata: Metadata,
  columns: CompatibleColumns,
  currentColumnKey: string,
  parameters?: { key: string; value: string }[] | null
) => {
  const currentValue = getFieldValue(row, currentColumnKey)

  // Extract parameters
  const columnsParam = parameters?.find((p) => p.key === 'columns')?.value

  // Skip validation if columns parameter is not provided
  if (!columnsParam) {
    return { value: row[currentColumnKey] }
  }

  // Split columns by comma and check if all of them have a value
  const columnsArray = columnsParam.split(',').map((col) => col.trim())
  const isRequired = columnsArray.every((column) => {
    const columnValue = getFieldValue(row, column)
    return columnValue != null && String(columnValue).trim() !== ''
  })

  // Get the current field label for error message
  const currentColumn = findColumnByKey(columns, currentColumnKey)
  const currentFieldLabel =
    hasKey(currentColumn) && typeof currentColumn.label === 'string'
      ? currentColumn.label
      : currentColumnKey

  // Get labels for the required columns
  const requiredColumnsLabels = columnsArray.map((column) => {
    const columnObj = findColumnByKey(columns, column)
    return hasKey(columnObj) && typeof columnObj.label === 'string' ? columnObj.label : column
  })

  // Only fail if is_required is true AND current field is blank
  const value =
    isRequired && (!currentValue || String(currentValue).trim() === '')
      ? createHookedRecordResult(
          currentValue,
          i18n.t('universal_importer.nuvo_importer.validations.required_with_all', {
            field: currentFieldLabel,
            columns: requiredColumnsLabels.join(', '),
          })
        )
      : row[currentColumnKey]

  return { value }
}

// Object containing all validation functions
export const validationFunctions: Record<string, ValidationFunction> = {
  identifier,
  range,
  policy,
  date_after_or_equal,
  within_n_days,
  required_with,
  required_with_all,
  // Add more validation functions as needed...
}
